import React from "react";
import { Clock, Film, Ruler, FileCheck, Download, Monitor } from "lucide-react";
import { FadeUp } from "@/components/motion";
import AdPreflight from "@/components/AdPreflight";

/**
 * Rules + the full Elegant sample set — one GIF per size, each displayed at
 * its exact on-site display dimensions (W × 168 for Sizes 2-5, 328 × 272 for
 * Size 1). All five GIFs: 49 frames, 5 messages, ~3 s hold per message.
 */

const RULES = [
  { Icon: Ruler, label: "Size", value: "Exact upload pixel size", hint: "See the table above for the five Elegant upload dimensions" },
  { Icon: FileCheck, label: "Format", value: "GIF, PNG or JPG only", hint: "No .mov, .mp4, .webp, .svg" },
  { Icon: Film, label: "Frames", value: "50 frames or fewer", hint: "The examples below use 49" },
  { Icon: Clock, label: "Weight", value: "Under 10 MB (raw)", hint: "Not compressed" },
];

const EXAMPLES = [
  { size: "1", file: "/examples/wm-example-elegant-s1.gif", upload: "656 × 544",  display: "328 × 272",  minScreen: "360",  aspect: "328 / 272" },
  { size: "2", file: "/examples/wm-example-elegant-s2.gif", upload: "1184 × 336", display: "592 × 168",  minScreen: "624",  aspect: "592 / 168" },
  { size: "3", file: "/examples/wm-example-elegant-s3.gif", upload: "1440 × 336", display: "720 × 168",  minScreen: "752",  aspect: "720 / 168" },
  { size: "4", file: "/examples/wm-example-elegant-s4.gif", upload: "1920 × 336", display: "960 × 168",  minScreen: "992",  aspect: "960 / 168" },
  { size: "5", file: "/examples/wm-example-elegant-s5.gif", upload: "2432 × 336", display: "1216 × 168", minScreen: "1248", aspect: "1216 / 168" },
  { size: "6", file: "/examples/wm-example-elegant-s6.gif", upload: "656 × 250", display: "328 × 125", minScreen: "All screens", aspect: "328 / 125", note: "WSA campaigns only" },
];

const ExampleRow = ({ size, file, upload, display, minScreen, aspect, note }) => {
  const [dispW, dispH] = display.split(" × ").map((n) => parseInt(n, 10));
  return (
    <FadeUp>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6" data-testid={`gif-example-s${size}`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] font-extrabold uppercase tracking-widest text-primary border border-primary/40 rounded-full px-3 py-1">
              Size {size}
            </span>
            {note && (
              <span
                className="font-mono text-[10px] font-extrabold uppercase tracking-widest text-amber-300 bg-amber-400/15 border border-amber-400/50 rounded-full px-3 py-1"
                data-testid={`gif-note-s${size}`}
              >
                {note}
              </span>
            )}
            <div>
              <p className="font-sans text-sm text-white">
                <span className="text-white/50">Display</span>{" "}
                <span className="font-semibold">{display}</span>
                <span className="text-white/30 mx-2">·</span>
                <span className="text-white/50">Upload</span>{" "}
                <span className="font-semibold">{upload}</span>
              </p>
              <p className="font-sans text-xs text-white/50 mt-0.5">
                <Monitor size={11} className="inline -mt-0.5 mr-1.5 text-white/40" />
                {minScreen === "All screens" ? "Shown on all screen sizes" : `Shown when viewport ≥ ${minScreen}px`}
              </p>
            </div>
          </div>
          <a
            href={file}
            download
            data-testid={`gif-download-s${size}`}
            className="shrink-0 inline-flex items-center gap-2 font-sans text-xs font-bold border border-white/20 text-white rounded-full pl-4 pr-3 py-2 hover:bg-primary hover:border-primary transition-colors"
          >
            Download GIF <Download size={12} />
          </a>
        </div>

        {/* The actual preview — rendered at the real display size and centered */}
        <div className="bg-white rounded-xl p-4 md:p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60 mb-3 text-center">
            Preview at display size · {display}
          </p>
          <div className="flex justify-center">
            <img
              src={file}
              alt={`Which Media Elegant Size ${size} example — ${display} display, ${upload} upload, 49 frames with five three-second messages`}
              width={dispW}
              height={dispH}
              className="rounded-md shadow-sm max-w-full h-auto"
              style={{ width: dispW, aspectRatio: aspect }}
            />
          </div>
        </div>
      </div>
    </FadeUp>
  );
};

const GifHowTo = () => (
  <section data-testid="gif-howto" className="bg-ink text-white">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <FadeUp>
        <p className="kicker mb-4" style={{ color: "hsl(var(--primary))" }}>Designer Toolkit</p>
        <h2 className="headline text-4xl md:text-6xl uppercase max-w-3xl">
          How to make a <span className="text-primary">compliant ad</span>
        </h2>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/80">
          Four simple rules — they apply to every format (GIF, PNG and JPG). Then press play on every
          Elegant size below — each preview is rendered at the actual on-page display dimensions, so
          you can see exactly how your creative will land.
        </p>
      </FadeUp>

      {/* Four rules */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="gif-rules">
        {RULES.map(({ Icon, label, value, hint }) => (
          <FadeUp key={label}>
            <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <Icon size={22} className="text-primary mb-4" />
              <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mb-2">{label}</p>
              <p className="headline text-xl text-white leading-snug">{value}</p>
              <p className="mt-2 text-sm text-white/60">{hint}</p>
            </div>
          </FadeUp>
        ))}
      </div>

      {/* All 5 Elegant sizes */}
      <div className="mt-14" data-testid="gif-examples">
        <FadeUp>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
            <div>
              <p className="kicker" style={{ color: "hsl(var(--primary))" }}>Examples — all 5 Elegant sizes</p>
              <p className="headline text-2xl md:text-3xl uppercase">Every size, 49 frames, 5 messages</p>
            </div>
            <p className="font-sans text-sm text-white/60 max-w-md">
              All five previews are rendered at the exact display dimensions from the table above.
              Each GIF holds every message for ~3 seconds — about 16 seconds total.
            </p>
          </div>
        </FadeUp>

        <div className="space-y-6">
          {EXAMPLES.map((ex) => <ExampleRow key={ex.size} {...ex} />)}
        </div>
      </div>

      {/* Timing clarification */}
      <FadeUp>
        <div className="mt-10 bg-primary text-primary-foreground rounded-2xl p-6 md:p-8" data-testid="gif-timing-note">
          <p className="kicker mb-3" style={{ color: "rgba(255,255,255,0.9)" }}>Important — on timing</p>
          <p className="headline text-xl md:text-2xl uppercase mb-3">
            Your GIF can run longer than 3 seconds.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-white/95 max-w-4xl">
            The 50-frame cap is <em>not</em> a 50-frames-per-second cap. Hold each still for about 3–4 seconds and
            you can comfortably land <span className="font-bold">five full messages</span> inside the budget — exactly
            as the examples above do. More hold-time per still = fewer, more memorable messages;
            less hold-time = more motion but less reading. Pick the mix your campaign needs.
          </p>
        </div>
      </FadeUp>

      {/* Pre-flight checker */}
      <div id="preflight" className="scroll-mt-24">
        <AdPreflight />
      </div>
    </div>
  </section>
);

export default GifHowTo;
